import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type { HealthCheckView, Incident, IncidentsView, MonitoringView } from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Notice,
  Skeleton,
} from '../components/ui.tsx';
import { HealthBadge, Meter, Technical } from '../components/operational.tsx';
import { usePolling, type PollInterval } from '../hooks/usePolling.ts';
import type { MessageKey } from '../i18n/messages.ts';
import '../components/operational.css';

/**
 * Monitoring, and what went wrong.
 *
 * Together on one screen because they are one question asked twice: "is my
 * service working, and if not, what is being done?" Splitting them puts the
 * answer to the second half behind a click that a worried customer has no
 * reason to know about.
 *
 * What this screen refuses to do is fill space. A server nobody has inspected
 * shows no processor figure and says so; a tunnel with no measured round trip
 * shows no number rather than a zero. Every gap here is a gap in what the
 * platform actually knows, said out loud, because a dashboard whose blanks are
 * indistinguishable from its measurements is a dashboard that cannot be used
 * to decide anything.
 */
export function Monitoring(): ReactNode {
  const { t, formatDateTime, formatNumber } = useI18n();
  const [interval, setInterval] = useAutoRefresh();

  // One poller for both reads, so the screen never shows health from one
  // moment beside incidents from another.
  const poll = usePolling(
    useCallback(async (): Promise<{ monitoring: MonitoringView; incidents: IncidentsView }> => {
      const [monitoring, incidents] = await Promise.all([api.monitoring(), api.incidents()]);
      return { monitoring, incidents };
    }, []),
    interval,
  );

  const view = poll.data?.monitoring;
  const incidents = poll.data?.incidents;

  if (!view) {
    return poll.failed ? (
      <Card>
        <EmptyState
          title={t('error.network')}
          action={
            <Button variant="secondary" onClick={poll.refresh}>
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    ) : (
      <Card>
        <Skeleton lines={7} />
      </Card>
    );
  }

  const unhealthy = [...view.panels, ...view.tunnels].filter(
    (resource) => resource.status === 'unhealthy' || resource.status === 'degraded',
  ).length;
  const nothing =
    view.servers.length === 0 && view.panels.length === 0 && view.tunnels.length === 0;

  return (
    <>
      <header className="page-header">
        <div>
          <h1>{t('monitoring.title')}</h1>
          <p className="muted">{t('monitoring.subtitle')}</p>
        </div>
        <div className="monitoring__controls">
          <label className="preferences__control">
            <span className="visually-hidden">{t('monitoring.autoRefresh')}</span>
            <select
              value={interval}
              onChange={(event) => setInterval(Number(event.target.value) as PollInterval)}
            >
              <option value={0}>{t('monitoring.autoOff')}</option>
              <option value={30_000}>{t('monitoring.auto30s')}</option>
              <option value={60_000}>{t('monitoring.auto1m')}</option>
              <option value={300_000}>{t('monitoring.auto5m')}</option>
            </select>
          </label>
          <Button variant="secondary" busy={poll.loading} onClick={poll.refresh}>
            {t('common.refresh')}
          </Button>
        </div>
      </header>

      {/* The age of what is on screen, always — not "now". With auto-refresh
          off this is the only way to know whether these figures are a minute
          or a day old. */}
      <p className="subtle monitoring__age">
        {poll.fetchedAt
          ? t('monitoring.lastChecked', { when: formatDateTime(poll.fetchedAt) })
          : null}
      </p>

      {/* A failed poll does not replace the figures; it is reported beside
          them, still showing the last ones that were real. */}
      {poll.failed ? <Notice tone="warning">{t('monitoring.refreshFailed')}</Notice> : null}

      {nothing ? (
        <Card>
          <EmptyState title={t('monitoring.nothing')} />
        </Card>
      ) : (
        <Notice tone={unhealthy === 0 ? 'success' : 'warning'}>
          {unhealthy === 0
            ? t('monitoring.allHealthy')
            : t('monitoring.needsAttention', { count: unhealthy })}
        </Notice>
      )}

      {view.servers.length > 0 ? (
        <Card>
          <CardHeader title={t('monitoring.servers')} />
          <div className="op-list">
            {view.servers.map((server) => (
              <div className="op-row" key={server.id}>
                <div className="op-row__head">
                  <div className="op-row__title">
                    <span>{server.name}</span>
                    {server.locationLabel ? (
                      <Badge tone="neutral">{server.locationLabel}</Badge>
                    ) : null}
                  </div>
                  <span className="muted">
                    {server.lastInventoryAt
                      ? t('monitoring.checkedAt', {
                          when: formatDateTime(server.lastInventoryAt),
                        })
                      : t('monitoring.notInspected')}
                  </span>
                </div>

                {server.usage ? (
                  <div className="op-row__meters">
                    <Meter
                      label={t('monitoring.load')}
                      used={server.usage.loadAverage1m}
                      total={server.usage.cpuCount}
                      format={(value) => formatNumber(Math.round(value * 100) / 100)}
                    />
                    <Meter
                      label={t('monitoring.memory')}
                      used={server.usage.memoryTotalMb - server.usage.memoryAvailableMb}
                      total={server.usage.memoryTotalMb}
                      format={(value) => megabytes(value, formatNumber)}
                    />
                    <Meter
                      label={t('monitoring.disk')}
                      used={server.usage.diskTotalMb - server.usage.diskAvailableMb}
                      total={server.usage.diskTotalMb}
                      format={(value) => megabytes(value, formatNumber)}
                    />
                  </div>
                ) : (
                  <p className="muted">{t('monitoring.notInspected')}</p>
                )}

                <Technical>
                  <div>server {server.id}</div>
                  <div>status {server.status}</div>
                  {server.usage ? <div>cpus {server.usage.cpuCount}</div> : null}
                </Technical>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {view.panels.length > 0 ? (
        <Card>
          <CardHeader title={t('monitoring.panels')} />
          <div className="op-list">
            {view.panels.map((panel) => (
              <div className="op-row" key={panel.id}>
                <div className="op-row__head">
                  <div className="op-row__title">
                    <span>{panel.domain ?? panel.adapterId}</span>
                    <HealthBadge status={panel.status} />
                  </div>
                  <span className="muted">
                    {panel.lastHealthAt
                      ? t('health.lastChecked', { when: formatDateTime(panel.lastHealthAt) })
                      : t('health.neverChecked')}
                  </span>
                </div>
                <Checks checks={panel.checks} />
                <Technical>
                  <div>panel {panel.id}</div>
                  <div>adapter {panel.adapterId}</div>
                  <div>server {panel.serverId}</div>
                </Technical>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {view.tunnels.length > 0 ? (
        <Card>
          <CardHeader title={t('monitoring.tunnels')} />
          <div className="op-list">
            {view.tunnels.map((tunnel) => (
              <div className="op-row" key={tunnel.id}>
                <div className="op-row__head">
                  <div className="op-row__title">
                    <span>{t('monitoring.nodes', { count: tunnel.nodeCount })}</span>
                    <HealthBadge status={tunnel.status} />
                  </div>
                  <span className="muted">
                    {tunnel.lastHealthAt
                      ? t('health.lastChecked', { when: formatDateTime(tunnel.lastHealthAt) })
                      : t('health.neverChecked')}
                  </span>
                </div>
                <div className="op-row__facts">
                  <span>
                    {t('monitoring.latency')}:{' '}
                    {tunnel.latencyMs === null ? (
                      <span className="muted">{t('common.unknown')}</span>
                    ) : (
                      `${formatNumber(Math.round(tunnel.latencyMs))} ms`
                    )}
                  </span>
                  <span>
                    {t('monitoring.packetLoss')}:{' '}
                    {tunnel.packetLossPercent === null ? (
                      <span className="muted">{t('common.unknown')}</span>
                    ) : (
                      `${formatNumber(tunnel.packetLossPercent)}%`
                    )}
                  </span>
                </div>
                <Checks checks={tunnel.checks} />
                <Technical>
                  <div>tunnel {tunnel.id}</div>
                  <div>adapter {tunnel.adapterId}</div>
                </Technical>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Incidents view={incidents} />
    </>
  );
}

/**
 * The individual checks behind a status.
 *
 * Shown in both modes, because "the panel answered but its database did not"
 * is not a technical detail — it is the difference between waiting and calling
 * somebody. Only the diagnostics attached to a check are expert-only, and
 * those only exist in the response when the account is allowed them.
 */
function Checks({ checks }: { checks: readonly HealthCheckView[] }): ReactNode {
  const { pick } = useI18n();
  if (checks.length === 0) return null;
  return (
    <ul className="incident__repairs">
      {checks.map((check) => (
        <li className="incident__repair" key={check.id}>
          <HealthBadge status={check.status} />
          <span>{pick(check.title)}</span>
          <span className="muted">{pick(check.evidence)}</span>
          {check.diagnostics ? (
            <Technical>
              <div>{check.diagnostics}</div>
            </Technical>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function Incidents({ view }: { view: IncidentsView | undefined }): ReactNode {
  const { t } = useI18n();
  if (!view) return null;

  return (
    <Card>
      <CardHeader title={t('incidents.title')} />
      {view.incidents.length === 0 ? (
        <p className="muted">{t('incidents.none')}</p>
      ) : (
        <div className="op-list">
          {view.incidents.map((incident) => (
            <IncidentRow key={incident.id} incident={incident} technical={view.technicalDetail} />
          ))}
        </div>
      )}
    </Card>
  );
}

const REPAIR_LABEL: Record<string, MessageKey> = {
  restart_panel_service: 'repair.restart_panel_service',
  restart_tunnel_interface: 'repair.restart_tunnel_interface',
  rotate_tunnel_keys: 'repair.rotate_tunnel_keys',
  lower_tunnel_mtu: 'repair.lower_tunnel_mtu',
  free_disk_space: 'repair.free_disk_space',
  renew_certificate: 'repair.renew_certificate',
  enable_ip_forwarding: 'repair.enable_ip_forwarding',
  reinstall_panel: 'repair.reinstall_panel',
  reboot_server: 'repair.reboot_server',
  none: 'repair.none',
};

const OUTCOME_LABEL: Record<string, MessageKey> = {
  repaired: 'repair.outcomeRepaired',
  failed: 'repair.outcomeFailed',
  still_unhealthy: 'repair.outcomeStillUnhealthy',
  escalated: 'repair.outcomeEscalated',
};

function IncidentRow({
  incident,
  technical,
}: {
  incident: Incident;
  technical: boolean;
}): ReactNode {
  const { t, pick, formatDateTime } = useI18n();

  return (
    <article className={`incident incident--${incident.status}`}>
      <div className="op-row__head">
        <div className="op-row__title">
          <Badge tone={incident.status === 'open' ? 'danger' : 'success'}>
            {t(incident.status === 'open' ? 'incidents.open' : 'incidents.resolved')}
          </Badge>
          <span>{pick(incident.summary)}</span>
        </div>
        <span className="muted">
          {incident.status === 'resolved' && incident.resolvedAt
            ? t('incidents.resolvedAt', { when: formatDateTime(incident.resolvedAt) })
            : t('incidents.detected', { when: formatDateTime(incident.detectedAt) })}
        </span>
      </div>

      {incident.resource.label ? (
        <p className="muted">
          {t('incidents.affected')}: {incident.resource.label}
        </p>
      ) : null}

      <div>
        <strong>{t('incidents.repairs')}</strong>
        {incident.repairs.length === 0 ? (
          // Without this the heading stood over an empty list, which reads as
          // "we tried" followed by nothing — the opposite of what happened.
          <p className="muted">{t('incidents.repairNone')}</p>
        ) : null}
        <ul className="incident__repairs">
          {incident.repairs.map((repair, index) => (
            <li className="incident__repair" key={`${repair.at}-${index}`}>
              <span>{t(REPAIR_LABEL[repair.action] ?? 'repair.none')}</span>
              <span className="muted">
                — {t(OUTCOME_LABEL[repair.outcome] ?? 'repair.outcomeEscalated')}
              </span>
              <span className="subtle">{formatDateTime(repair.at)}</span>
              {repair.detail || repair.reason ? (
                <Technical>
                  {repair.reason ? <div>{repair.reason}</div> : null}
                  {repair.detail ? <div>{repair.detail}</div> : null}
                </Technical>
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      {/* Said once, where somebody would otherwise conclude there was nothing
          to see. A customer account genuinely cannot be shown this, and the
          honest sentence is better than an empty expander. */}
      {!technical ? <p className="subtle">{t('incidents.technicalHidden')}</p> : null}

      <Technical>
        <div>
          {incident.resource.kind} {incident.resource.id}
        </div>
        <div>last seen {incident.lastSeenAt}</div>
      </Technical>
    </article>
  );
}

/**
 * A size in the reader's own digits.
 *
 * Takes the formatter rather than calling `toFixed`, because a Persian page
 * that renders ۳٫۸ گیگابایت beside "3.1 GB" is a page that was translated and
 * not localised. Every figure on this screen goes through `Intl`; the ones
 * inside `t()` already did, and these are the ones that were bypassing it.
 */
function megabytes(value: number, formatNumber: (value: number) => string): string {
  if (value >= 1024) return `${formatNumber(Math.round((value / 1024) * 10) / 10)} GB`;
  return `${formatNumber(Math.round(value))} MB`;
}

/**
 * How often to ask again, remembered per browser.
 *
 * Off by default and stored rather than defaulted on, because the cost of
 * polling is paid by the customer's phone and not by us. A stored value that
 * cannot be read is off, which is the safe direction.
 */
const STORAGE_KEY = 'paneltunnel.monitoring.refresh';
const ALLOWED: readonly PollInterval[] = [0, 30_000, 60_000, 300_000];

function useAutoRefresh(): [PollInterval, (value: PollInterval) => void] {
  const [interval, setIntervalState] = useState<PollInterval>(() => {
    try {
      const stored = Number(window.localStorage.getItem(STORAGE_KEY));
      return (ALLOWED.find((value) => value === stored) ?? 0) as PollInterval;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(interval));
    } catch {
      // Private browsing. The choice lasts as long as the tab.
    }
  }, [interval]);

  return [interval, setIntervalState];
}
